import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ThemeMode = 'light' | 'dark' | 'auto';

const THEME_KEY = 'app-theme';

/**
 * ThemeService
 *
 * Gerencia o tema claro/escuro da aplicação.
 * - Persiste via localStorage (mais confiável que cookies para preferências UI)
 * - Respeita `prefers-color-scheme` quando em modo "auto"
 * - Aplica `data-bs-theme` no <html> (Bootstrap 5.3 nativo)
 * - Aplica `data-layout-mode` no <body> (compatibilidade com template legado)
 * - Notifica o WhiteLabelService sobre mudanças de tema
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly themeSubject: BehaviorSubject<ThemeMode>;
  readonly theme$: Observable<ThemeMode>;

  private resolvedTheme: 'light' | 'dark' = 'light';
  private mediaQuery: MediaQueryList;

  /** Callback para notificar mudanças de tema (evita dependência circular) */
  private themeChangeCallback: ((theme: 'light' | 'dark') => void) | null = null;

  constructor() {
    this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const saved = this.loadSaved();
    this.themeSubject = new BehaviorSubject<ThemeMode>(saved);
    this.theme$ = this.themeSubject.asObservable();

    this.applyTheme(saved);

    // Escuta mudanças na preferência do sistema
    this.mediaQuery.addEventListener('change', () => {
      if (this.themeSubject.value === 'auto') {
        this.applyTheme('auto');
      }
    });
  }

  /**
   * Registra callback para notificar sobre mudanças de tema.
   * Usado pelo WhiteLabelService para atualizar logos/cores por tema.
   */
  onThemeChange(callback: (theme: 'light' | 'dark') => void): void {
    this.themeChangeCallback = callback;
    // Notifica imediatamente com o tema atual
    callback(this.resolvedTheme);
  }

  get currentTheme(): ThemeMode {
    return this.themeSubject.value;
  }

  get effectiveTheme(): 'light' | 'dark' {
    return this.resolvedTheme;
  }

  setTheme(mode: ThemeMode): void {
    localStorage.setItem(THEME_KEY, mode);
    this.themeSubject.next(mode);
    this.applyTheme(mode);
  }

  toggle(): void {
    const next = this.resolvedTheme === 'light' ? 'dark' : 'light';
    this.setTheme(next);
  }

  // ─── Private ────────────────────────────────────────────────────────────────

  private loadSaved(): ThemeMode {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === 'light' || saved === 'dark' || saved === 'auto') {
      return saved;
    }
    return 'light';
  }

  private applyTheme(mode: ThemeMode): void {
    this.resolvedTheme = mode === 'auto'
      ? (this.mediaQuery.matches ? 'dark' : 'light')
      : mode;

    document.documentElement.setAttribute('data-bs-theme', this.resolvedTheme);
    document.body.setAttribute('data-layout-mode', this.resolvedTheme);

    // Notifica o WhiteLabelService sobre a mudança de tema
    if (this.themeChangeCallback) {
      this.themeChangeCallback(this.resolvedTheme);
    }
  }
}
